import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — немецкий текст
const lang = 'de';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Ein Event wird in einem Durchgang über das Formular „Neues Event erstellen“ angelegt. Wähle zuerst den
        Eventtyp und die Art, wie Rennen ins Event kommen — davon hängt ab, welche Felder das Formular zeigt.
        Unten findest du jeden Schritt und jede Variante mit Screenshots.
      </p>

      <Toc
        title="Inhalt"
        items={[
          ['access', 'Bevor du startest'],
          ['choose', 'Die passende Event-Variante wählen'],
          ['basics', 'Schritt 1. Grundlagen: Name, Beschreibung, Mitorganisatoren'],
          ['type', 'Schritt 2. Eventtyp'],
          ['status', 'Schritt 3. Status und Bild'],
          ['registration', 'Schritt 4. Anmeldung und Sichtbarkeit'],
          ['scoring', 'Schritt 5. Wertung und Punkte'],
          ['wildcards', 'Schritt 6. Wildcards (Rennen und Turnier)'],
          ['sync', 'Variante A. Rennen über die Suchvorlage'],
          ['manual', 'Variante B. Rennen anderer ROUVY-Nutzer'],
          ['open', 'Variante C. Offenes Turnier'],
          ['official', 'Variante D. Offizielles Event'],
          ['after', 'Nach dem Erstellen: Bearbeiten und Mitorganisatoren'],
          ['problems', 'Häufige Fehler']
        ]}
      />

      <section id="access">
        <h2>Bevor du startest</h2>
        <ul>
          <li>
            Die Schaltfläche <strong>„Event erstellen“</strong> <Num>1</Num> auf der Seite „Events“ sehen nur Konten,
            die Events erstellen dürfen. Fehlt sie, wende dich an einen Administrator.
          </li>
          <li>Die Schaltfläche <strong>„Eventtypen“</strong> <Num>2</Num> öffnet eine kurze Übersicht der Typen.</li>
          <li>
            Der Typ <strong>„Offenes Turnier“</strong> steht nur Konten mit besonderem Zugang zur Verfügung. Andere sehen
            ihn nicht in der Typenliste.
          </li>
          <li>Damit Rennen über die Suchvorlage gefunden werden, muss beim Eventautor oder einem Mitorganisator ROUVY verbunden sein.</li>
        </ul>
        <Shot
          name="events-list"
          alt="Seite „Events“: Schaltflächen „Eventtypen“ und „Event erstellen“"
          caption="Die Seite „Events“. Das Stiftsymbol in der Eventzeile (3) öffnet die Bearbeitung — sichtbar für den Eventautor und Administratoren."
        />
      </section>

      <section id="choose">
        <h2>Die passende Event-Variante wählen</h2>
        <p>Die Variante ergibt sich aus zwei Entscheidungen: dem <strong>Typ</strong> und <strong>wie Rennen hinzugefügt werden</strong>.</p>

        <h3>Eventtypen</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Typ</th>
                <th>Strecken</th>
                <th>Zeitplan</th>
                <th>Limit Rennen pro Etappe</th>
                <th>Wildcards</th>
                <th>Wofür</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Gruppenfahrt</strong></td>
                <td>1</td>
                <td>Ja</td>
                <td>—</td>
                <td>—</td>
                <td>Gemeinsame Ausfahrt oder Training mit Freunden</td>
              </tr>
              <tr>
                <td><strong>Rennen</strong></td>
                <td>1</td>
                <td>Ja</td>
                <td>Ja</td>
                <td>Ja</td>
                <td>Ein einzelnes Rennen, auch mit mehreren Starts</td>
              </tr>
              <tr>
                <td><strong>Turnier</strong></td>
                <td>1 oder mehr</td>
                <td>Ja</td>
                <td>Ja</td>
                <td>Ja</td>
                <td>Eine Serie von Etappen auf verschiedenen Strecken</td>
              </tr>
              <tr>
                <td><strong>Offenes Turnier</strong></td>
                <td>1 oder mehr</td>
                <td>Nein, nur ein Zeitraum</td>
                <td>—</td>
                <td>—</td>
                <td>Teilnehmer fahren die Strecken, wann sie wollen; die beste Zeit zählt</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">„Strecken“ — wie viele verschiedene ROUVY-Strecken das Event haben kann. Eine Strecke kann mehrere Starts haben.</p>

        <h3>Wege, Rennen hinzuzufügen</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Weg</th>
                <th>Wer erstellt die Rennen in ROUVY</th>
                <th>Eventtypen</th>
                <th>So aktivierst du ihn</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Suchvorlage</strong></a></td>
                <td>Eventautor oder Mitorganisatoren</td>
                <td>Gruppenfahrt, Rennen, Turnier</td>
                <td>Standard</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. Manuell</strong></a></td>
                <td>Beliebige ROUVY-Nutzer</td>
                <td>Gruppenfahrt, Rennen, Turnier</td>
                <td>Kästchen „Rennen wurden von anderen ROUVY-Nutzern erstellt“</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Strecken ohne Zeitplan</strong></a></td>
                <td>Jeder Teilnehmer für sich</td>
                <td>Offenes Turnier</td>
                <td>Typ „Offenes Turnier“ wählen</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Offizielles Event</strong></a></td>
                <td>ROUVY</td>
                <td>Alle außer offenem Turnier</td>
                <td>Status „Offiziell“</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          Wie Rennen hinzugefügt werden, legst du beim Erstellen fest — später lässt es sich nicht ändern. Hast du
          dich vertan, erstelle das Event neu.
        </Note>
      </section>

      <section id="basics">
        <h2>Schritt 1. Grundlagen: Name, Beschreibung, Mitorganisatoren</h2>
        <Shot name="create-basics" alt="Oberer Teil des Formulars zum Erstellen eines Events" caption="Oberer Teil des Formulars für die Variante mit Suchvorlage" />
        <ol>
          <li><Num>1</Num> <strong>Eventname</strong> — Pflichtfeld. Ohne Suchvorlage werden ROUVY-Rennen nach diesem Namen gesucht.</li>
          <li>
            <Num>2</Num> <strong>Rennen wurden von anderen ROUVY-Nutzern erstellt</strong> — nur für <a href="#manual">Variante B</a> aktivieren.
            Dann verschwindet das Feld für die Suchvorlage und eine Rennliste erscheint.
          </li>
          <li>
            <Num>3</Num> <strong>Rouvy-Suchvorlage</strong> — der Namensteil, den alle deine ROUVY-Rennen dieses Events gemeinsam haben,
            z. B. <code>Autumn Cup</code>. Siehe <a href="#sync">Variante A</a>.
          </li>
          <li><Num>4</Num> <strong>Mitorganisatoren</strong> — Nutzer, die ebenfalls Rennen hinzufügen können. Auch ihre ROUVY-Rennen werden über die Vorlage gefunden.</li>
          <li><Num>5</Num> <strong>Beschreibung</strong> — Regeln, Format, Links. Teilnehmer sehen sie auf der Eventseite.</li>
        </ol>

        <h3>Mitorganisator hinzufügen</h3>
        <p>Tippe einen Namen oder Nicknamen (ab 2 Zeichen) und wähle die Person aus der Liste. Ausgewählte erscheinen als Chips; das Kreuz entfernt sie.</p>
        <Shot name="co-organizers" alt="Suche nach Mitorganisatoren" caption="Ein Mitorganisator ist hinzugefügt, ein zweiter wird aus der Liste gewählt" />
      </section>

      <section id="type">
        <h2>Schritt 2. Eventtyp</h2>
        <p>Unter der Typenliste zeigt das Formular einen Hinweis zum gewählten Typ. Der Typ bestimmt, welche Felder folgen.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Typ Gruppenfahrt" caption="Gruppenfahrt: eine Strecke, kein Rennlimit, keine Wildcards" />
          <Shot name="type-race" alt="Typ Rennen" caption="Rennen: eine Strecke, dazu Rennen pro Etappe und Wildcards" />
          <Shot name="type-tournament" alt="Typ Turnier" caption="Turnier: mehrere Strecken, dieselben Zusatzfelder wie beim Rennen" />
          <Shot name="type-open_tournament" alt="Typ Offenes Turnier" caption="Offenes Turnier: dazu der Turnierzeitraum" />
        </div>
      </section>

      <section id="status">
        <h2>Schritt 3. Status und Bild</h2>
        <Shot name="status-image" alt="Status und Bild des Events" />
        <ol>
          <li>
            <Num>1</Num> <strong>Status</strong>: „Benutzerdefiniert“ passt fast immer. „Offiziell“ ist für offizielle ROUVY-Events,
            siehe <a href="#official">Variante D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Bild</strong> — das Titelbild des Events. „Bild hochladen“, bis 5 MB.
            „Bild entfernen“ löscht das gewählte Bild.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Schritt 4. Anmeldung und Sichtbarkeit</h2>
        <Shot name="registration" alt="Anmeldeeinstellungen" caption="Ein Turnier: „Rennen pro Etappe“ gibt es nur bei Rennen und Turnieren" />
        <ol>
          <li>
            <Num>1</Num> <strong>Anmeldeschluss (Stunden vor dem Start)</strong> — wie viele Stunden vor dem ersten Rennen
            die Anmeldung schließt. Leer — Anmeldung auch nach dem Start möglich.
          </li>
          <li>
            <Num>2</Num> <strong>Rennen pro Etappe</strong> (Rennen und Turnier) — wie viele Rennen einer Etappe ein Teilnehmer für die Wertung fahren darf.
            Teilnehmer melden sich für bestimmte Rennen an, und nur diese zählen. Leer — alle Rennen zählen.
          </li>
          <li><Num>3</Num> <strong>Langes Event</strong> — die Anmeldung bleibt die ganze Zeit offen, auch nach allen Rennen.</li>
          <li>
            <Num>4</Num> <strong>Verstecktes Event</strong> — in der Eventliste sehen es nur Administratoren und Organisatoren.
            Ergebnisse werden normal berechnet, und der direkte Link funktioniert für alle.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Schritt 5. Wertung und Punkte</h2>
        <Shot name="scoring" alt="Wertung und Punkte" caption="Die Teamwertung ist aktiv — Teamfelder sind erschienen" />
        <ol>
          <li><Num>1</Num> <strong>Einzelwertung</strong> und/oder <strong>Teamwertung</strong>. Beide können aktiv sein.</li>
          <li><Num>2</Num> <strong>Punkte für Platz 1</strong> — was der Etappensieger bekommt (standardmäßig 1000).</li>
          <li><Num>3</Num> <strong>Punkteabstand</strong> — wie viel weniger jeder folgende Platz bekommt (standardmäßig 10).</li>
          <li><Num>4</Num> <strong>Gewertete Teammitglieder</strong> (nur Teamwertung) — wie viele beste Ergebnisse eines Teams zählen.</li>
          <li>
            <Num>5</Num> <strong>Max. Teammitglieder</strong> (nur Teamwertung) — wie viele Fahrer eines Teams sich anmelden können.
            Leer — keine Begrenzung.
          </li>
          <li><Num>6</Num> <strong>Erstellen</strong> — speichert das Event und führt zurück zur Eventliste.</li>
        </ol>
        <Note>
          Punkte für einen Platz: <code>Punkte für Platz 1 − (Platz − 1) × Abstand</code>, aber nicht unter 0.
          Bei 1000 und 10: Platz 1 — 1000, Platz 2 — 990, Platz 10 — 910, ab Platz 101 — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Schritt 6. Wildcards (Rennen und Turnier)</h2>
        <p>Eine Wildcard erlaubt es, eine Etappe außerhalb des Zeitplans zu fahren. Den Block gibt es nur bei benutzerdefinierten Events vom Typ Rennen und Turnier.</p>
        <ol>
          <li><strong>Wildcards (max. Anzahl)</strong> — wie viele Wildcards ein Teilnehmer im Event nutzen kann. Leer — keine Wildcards.</li>
          <li>
            <strong>Wer erstellt die Wildcard</strong> (erscheint bei mehr als 0 Wildcards):
            <ul>
              <li><strong>Vom Organisator erstellt</strong> — der Teilnehmer nennt Datum und Uhrzeit, der Organisator legt dafür ein ROUVY-Rennen an und genehmigt die Anfrage.</li>
              <li><strong>Vom Teilnehmer erstellt</strong> — der Teilnehmer legt nach deinen Vorgaben ein ROUVY-Rennen an und schickt den Link; der Organisator genehmigt oder lehnt ab.</li>
            </ul>
          </li>
          <li>
            <strong>Feld für die Voraussetzungen</strong> (nur „Vom Teilnehmer erstellt“) — was das Rennen des Teilnehmers erfüllen muss: Strecke, Einstellungen, Zeit.
            Teilnehmer sehen diesen Text, wenn sie eine Anfrage stellen.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wildcards vom Organisator erstellt" caption="Vom Organisator erstellt" />
          <Shot name="wildcards-participant" alt="Wildcards vom Teilnehmer erstellt" caption="Vom Teilnehmer erstellt: das Voraussetzungsfeld erscheint" />
        </div>
        <p>Über jede Anfrage erhält der Organisator eine Benachrichtigung.</p>
      </section>

      <section id="sync">
        <h2>Variante A. Rennen über die Suchvorlage</h2>
        <p>Das ist der Standard. Du (oder Mitorganisatoren) erstellst Rennen in ROUVY, und das System findet sie über die Vorlage und fügt sie dem Event hinzu.</p>
        <ol className="guide-steps">
          <li>Erstelle Rennen in ROUVY mit einem gemeinsamen Namensteil, z. B. <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Lass das Kästchen „Rennen wurden von anderen ROUVY-Nutzern erstellt“ <strong>deaktiviert</strong>.</li>
          <li>Trage den gemeinsamen Teil in <strong>„Rouvy-Suchvorlage“</strong> ein: <code>Autumn Cup</code>. Leer — der Eventname wird verwendet.</li>
          <li>Füge Mitorganisatoren hinzu, wenn auch andere Rennen anlegen.</li>
          <li>Fülle die übrigen Schritte aus und klicke auf <strong>„Erstellen“</strong>.</li>
          <li>Die Suche nach Rennen startet direkt nach dem Erstellen im Hintergrund. Die Rennen erscheinen nach einigen Minuten — prüfe sie in der Eventbearbeitung.</li>
        </ol>
        <p>In der Eventbearbeitung gibt es den Synchronisationsblock und die Liste der gefundenen Rennen:</p>
        <Shot name="edit-sync" alt="Synchronisation und hinzugefügte Rennen" />
        <ol>
          <li><Num>1</Num> <strong>Strecken synchronisieren</strong> — erneut suchen, z. B. nachdem du neue Rennen in ROUVY angelegt hast. Die Seite kann geschlossen werden.</li>
          <li><Num>2</Num> <strong>Liste aktualisieren</strong> — das Ergebnis der Synchronisation laden, ohne die Seite neu zu laden.</li>
          <li><Num>3</Num> <strong>Löschen</strong> — ein überflüssiges Rennen aus dem Event entfernen.</li>
        </ol>
        <Note>Wenn du Eventname oder Vorlage änderst und speicherst, startet die Suche automatisch.</Note>
      </section>

      <section id="manual">
        <h2>Variante B. Rennen anderer ROUVY-Nutzer</h2>
        <p>Haben weder du noch Mitorganisatoren die ROUVY-Rennen erstellt, findet die Vorlagensuche sie nicht. Dann werden Rennen manuell hinzugefügt.</p>
        <ol className="guide-steps">
          <li>Aktiviere <strong>„Rennen wurden von anderen ROUVY-Nutzern erstellt“</strong>. Das Vorlagenfeld verschwindet, der Block „Rennen des Events“ erscheint.</li>
          <li>
            Fülle für jedes Rennen aus:
            <ul>
              <li><strong>ROUVY-Strecke*</strong> — Strecken-ID oder Link wie <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Name der Strecke</strong> — optional, falls du einen eigenen willst;</li>
              <li><strong>Link zum ROUVY-Rennen*</strong> — <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Name des Rennens</strong> — optional, leer — wird aus ROUVY übernommen;</li>
              <li><strong>Startdatum und -zeit*</strong> — in deiner Zeitzone.</li>
            </ul>
          </li>
          <li><strong>„Rennen hinzufügen“</strong> fügt eine Zeile hinzu, <strong>„Entfernen“</strong> löscht sie. Leere Zeilen werden nicht gespeichert.</li>
          <li>Klicke auf <strong>„Erstellen“</strong>. Scheitert ein Rennen, wird das Event nicht erstellt, und der Fehler nennt die Zeilennummer.</li>
        </ol>
        <Shot name="manual-create" alt="Rennen des Events beim Erstellen" caption="Die Rennliste beim Erstellen des Events" />
        <p>Rennen lassen sich auch später hinzufügen: In der Eventbearbeitung gibt es ein Formular für ein Rennen <Num>1</Num>. Ein hinzugefügtes Rennen erscheint sofort in der Liste <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Rennen in der Bearbeitung hinzufügen" />
      </section>

      <section id="open">
        <h2>Variante C. Offenes Turnier</h2>
        <p>
          Ein offenes Turnier hat keinen Zeitplan. Jeder Teilnehmer legt in ROUVY ein Rennen (Race) auf einer Turnierstrecke an und fährt es, wann er will.
          Eine Fahrt zählt nach Strecke und Renntyp, in die Wertung geht die beste Zeit aller Versuche.
        </p>
        <ol className="guide-steps">
          <li>Wähle im „Eventtyp“ <strong>„Offenes Turnier“</strong>.</li>
          <li>
            Lege den <strong>Turnierzeitraum</strong> fest: „Turnierbeginn“ und „Turnierende“. Fahrten außerhalb zählen nicht.
            Beides ist optional: ohne Grenzen läuft das Turnier unbefristet. Das Ende muss nach dem Beginn liegen.
          </li>
          <li>Fülle die übrigen Schritte aus und klicke auf <strong>„Erstellen“</strong>.</li>
          <li>Öffne die Eventbearbeitung und füge Strecken hinzu (siehe unten).</li>
        </ol>
        <Note kind="warning">Strecken eines offenen Turniers werden erst nach dem Erstellen des Events hinzugefügt, in der Eventbearbeitung.</Note>
        <Shot name="edit-open" alt="Strecken des offenen Turniers" />
        <ol>
          <li><Num>1</Num> Füge den Link einer ROUVY-Streckenseite ein: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Klicke auf <strong>„Strecke hinzufügen“</strong>.</li>
          <li><Num>3</Num> Die Strecke gehört nun zum Turnier. „Löschen“ entfernt sie.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Variante D. Offizielles Event</h2>
        <p>
          Der Status <strong>„Offiziell“</strong> ist für offizielle ROUVY-Events. Ihre Rennen verwaltet ROUVY, deshalb ist das Formular kürzer:
          kein Kästchen für manuelle Rennen, keine Suchvorlage, keine Mitorganisatoren und keine Wildcards.
        </p>
        <Shot name="official" alt="Formular eines offiziellen Events" caption="Status „Offiziell“ — überflüssige Blöcke sind ausgeblendet" />
      </section>

      <section id="after">
        <h2>Nach dem Erstellen: Bearbeiten und Mitorganisatoren</h2>
        <ul>
          <li>Die Bearbeitung öffnest du über das Stiftsymbol in der Eventzeile auf der Seite „Events“. Alle Einstellungen ändern der Eventautor und Administratoren.</li>
          <li>Wie Rennen hinzugefügt werden (das Kästchen „Rennen wurden von anderen ROUVY-Nutzern erstellt“), lässt sich in der Bearbeitung nicht ändern.</li>
          <li>Mitorganisatoren sehen eine vereinfachte Seite: Sie können Rennen hinzufügen (Synchronisation starten oder manuell eintragen) und die von ihnen hinzugefügten löschen. Andere Einstellungen stehen ihnen nicht zur Verfügung.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Seite für Mitorganisatoren" caption="So sieht ein Mitorganisator das Event: löschen kann er nur sein eigenes Rennen" />
      </section>

      <section id="problems">
        <h2>Häufige Fehler</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Meldung oder Situation</th>
                <th>Ursache und Lösung</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>„Der Eventname ist erforderlich“</td>
                <td>Fülle „Eventname“ aus.</td>
              </tr>
              <tr>
                <td>„Gruppenfahrten und Rennen können nur eine Strecke haben“</td>
                <td>Das Event hat Rennen auf verschiedenen Strecken. Wähle den Typ Turnier oder entferne überzählige Rennen.</td>
              </tr>
              <tr>
                <td>„Das Turnier muss nach seinem Beginn enden“</td>
                <td>Korrigiere den Zeitraum des offenen Turniers.</td>
              </tr>
              <tr>
                <td>„Gib für jedes Rennen Strecke, Link zum ROUVY-Rennen und Startdatum/-zeit an“</td>
                <td>In einer Zeile der manuellen Rennen fehlt ein Pflichtfeld. Fülle es aus oder entferne die Zeile.</td>
              </tr>
              <tr>
                <td>Offenes Turnier wird beim Speichern abgelehnt</td>
                <td>Das Konto hat keinen Zugang zu offenen Turnieren. Wähle einen anderen Typ.</td>
              </tr>
              <tr>
                <td>Nach der Synchronisation keine Rennen</td>
                <td>
                  Prüfe, ob die Namen der ROUVY-Rennen die Vorlage enthalten, ob du oder Mitorganisatoren sie erstellt haben
                  und ob dein ROUVY verbunden ist. Warte einige Minuten und klicke auf „Liste aktualisieren“.
                  Hat jemand anderes die Rennen erstellt, brauchst du <a href="#manual">Variante B</a> — erstelle das Event neu.
                </td>
              </tr>
              <tr>
                <td>Keine Schaltfläche „Event erstellen“</td>
                <td>Das Konto darf keine Events erstellen — wende dich an einen Administrator.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
